// src/components/Dashboard.js - Fixed with proper User Management integration
import PaymentCollectionDashboard from './PaymentCollectionDashboard';
import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { apiService } from "../services/apiService";
import { Link } from "react-router-dom";
import Orders from "./Orders";
import Inventory from "./Inventory";
import Payments from "./Payments";
import ProductList from "./ProductList";
import UserManagement from "./UserManagement"; // ADD THIS IMPORT
import Analytics from "./Analytics";
import RazorpayPayment from './RazorpayPayment';
import ExternalPayment from './ExternalPayment'; // ← ADD THIS LINE

const Dashboard = () => {
  const { user, logout, tokens } = useAuth();
  const [activeSection, setActiveSection] = useState("overview");
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [userRole, setUserRole] = useState("USER");

  // Remove the user creation modal state since UserManagement component handles this
  // const [showUserCreation, setShowUserCreation] = useState(false);
  // const [newUser, setNewUser] = useState({
  //   username: '',
  //   password: '',
  //   role: 'USER'
  // });

  // Products state
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Get user role from token
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        const groups = payload["cognito:groups"] || [];
        setUserRole(groups.includes("ADMIN") ? "ADMIN" : "USER");
      } catch (error) {
        console.error("Error parsing token:", error);
        setUserRole("USER");
      }
    }
  }, [tokens]);

  const isAdmin = userRole === "ADMIN";

  useEffect(() => {
    if (activeSection === "overview") {
      loadDashboardSummary();
    }
  }, [activeSection]);

  const loadDashboardSummary = async () => {
    try {
      setLoading(true);
      // Use enhanced dashboard summary if available for admins
      const summary = isAdmin
        ? await apiService
            .getAdminDashboardSummary()
            .catch(() => apiService.getDashboardSummary())
        : await apiService.getDashboardSummary();
      setDashboardSummary(summary);
    } catch (err) {
      console.error("Failed to load dashboard summary:", err);
    } finally {
      setLoading(false);
    }
  };

  // Remove the handleCreateUser function since it's handled in UserManagement component

  const handleSectionClick = async (section) => {
    // Check admin access for user management
    if (section === "userManagement" && !isAdmin) {
      setError("Admin access required for User Management");
      return;
    }

    setActiveSection(section);
    setError(null);

    // Sections that don't need API calls
    if (
      section === "userInfo" ||
      section === "overview" ||
      section === "userManagement"
    ) {
      setApiData(null);
      return;
    }

    setLoading(true);
    try {
      let data;
      switch (section) {
        case "userInfoAPI":
          data = await apiService.getUserInfo();
          break;
        case "protected":
          data = await apiService.getProtectedData();
          break;
        case "products":
          data = await apiService.getAllProducts();
          setProducts(data);
          break;
        case "productsPage":
          data = await apiService.getAllProducts();
          setProducts(data);
          break;
        default:
          data = null;
      }
      setApiData(data);
    } catch (err) {
      setError(`Failed to fetch ${section}: ${err.message}`);
      setApiData(null);
    } finally {
      setLoading(false);
    }
  };

  // Filter products for the products page view
  const filteredProducts = products.filter((product) => {
    const matchesCategory =
      selectedCategory === "all" || product.category === selectedCategory;
    const matchesSearch =
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.category.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const categories = [
    "all",
    "electronics",
    "clothing",
    "books",
    "home",
    "sports",
    "beauty",
  ];

  // Remove the UserCreationModal component since UserManagement handles this

  const renderContent = () => {
    if (loading && activeSection !== "userManagement") {
      return <div className="content-loading">Loading...</div>;
    }

    if (error) {
      return (
        <div className="content-error">
          <h3>Error</h3>
          <p>{error}</p>
          {!error.includes("Admin access required") && (
            <button
              onClick={() => handleSectionClick(activeSection)}
              className="retry-btn"
            >
              Retry
            </button>
          )}
        </div>
      );
    }

    switch (activeSection) {
      case "overview":
        return (
          <div className="content-section">
            <h2>Dashboard Overview</h2>
            <div className="role-indicator">
              <span className={`role-badge ${isAdmin ? "admin" : "user"}`}>
                {userRole} Role
              </span>
            </div>
            {dashboardSummary ? (
              <div className="dashboard-overview">
                <div className="overview-stats">
                  <div className="stats-grid">
                    <div className="stat-card">
                      <h3>Total Orders</h3>
                      <div className="stat-number">
                        {dashboardSummary.totalOrders}
                      </div>
                      <Link to="/orders" className="stat-link">
                        View Orders →
                      </Link>
                    </div>
                    <div className="stat-card">
                      <h3>Total Payments</h3>
                      <div className="stat-number">
                        {dashboardSummary.totalPayments}
                      </div>
                      <span className="stat-link">View Payments →</span>
                    </div>
                    <div className="stat-card">
                      <h3>Products in Inventory</h3>
                      <div className="stat-number">
                        {dashboardSummary.totalProducts}
                      </div>
                      <Link to="/inventory" className="stat-link">
                        View Inventory →
                      </Link>
                    </div>
                    <div className="stat-card">
                      <h3>Low Stock Items</h3>
                      <div className="stat-number warning">
                        {dashboardSummary.lowStockItems?.length || 0}
                      </div>
                      <Link to="/inventory" className="stat-link">
                        Manage Stock →
                      </Link>
                    </div>
                    {/* Show user stats only for admins */}
                    {isAdmin && dashboardSummary.totalUsers !== undefined && (
                      <div className="stat-card">
                        <h3>Total Users</h3>
                        <div className="stat-number">
                          {dashboardSummary.totalUsers}
                        </div>
                        <span
                          className="stat-link"
                          onClick={() => handleSectionClick("userManagement")}
                          style={{ cursor: "pointer" }}
                        >
                          Manage Users →
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="recent-activities">
                  <div className="activity-section">
                    <h3>Recent Orders</h3>
                    {dashboardSummary.recentOrders?.length > 0 ? (
                      <div className="activity-list">
                        {dashboardSummary.recentOrders
                          .slice(0, 3)
                          .map((order) => (
                            <div key={order.id} className="activity-item">
                              <span className="activity-title">
                                Order #{order.id}
                              </span>
                              <span className="activity-details">
                                Product {order.productId} × {order.quantity} - $
                                {(order.quantity * order.price).toFixed(2)}
                              </span>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <p className="no-activity">No recent orders</p>
                    )}
                    <Link to="/orders" className="view-all-link">
                      View All Orders
                    </Link>
                  </div>

                  <div className="activity-section">
                    <h3>Recent Payments</h3>
                    {dashboardSummary.recentPayments?.length > 0 ? (
                      <div className="activity-list">
                        {dashboardSummary.recentPayments
                          .slice(0, 3)
                          .map((payment) => (
                            <div key={payment.id} className="activity-item">
                              <span className="activity-title">
                                Payment #{payment.id}
                              </span>
                              <span className="activity-details">
                                ${payment.amount?.toFixed(2)} - {payment.status}
                              </span>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <p className="no-activity">No recent payments</p>
                    )}
                    <span className="view-all-link">View All Payments</span>
                  </div>
                </div>

                {dashboardSummary.lowStockItems?.length > 0 && (
                  <div className="alerts-section">
                    <h3>⚠️ Stock Alerts</h3>
                    <div className="alert-list">
                      {dashboardSummary.lowStockItems
                        .slice(0, 5)
                        .map((item) => (
                          <div key={item.productId} className="alert-item">
                            <span className="alert-title">
                              Product {item.productId}
                            </span>
                            <span className="alert-details">
                              Only {item.quantity} units left
                            </span>
                          </div>
                        ))}
                    </div>
                    <Link to="/inventory" className="view-all-link">
                      Manage Inventory
                    </Link>
                  </div>
                )}
              </div>
            ) : (
              <div className="overview-loading">
                <p>Loading dashboard data...</p>
              </div>
            )}
          </div>
        );
      case "makePayment":
        return (
          <div className="content-section">
            <RazorpayPayment />
          </div>
        );
      case "userManagement":
        return (
          <div className="content-section">
            {/* Render the full UserManagement component */}
            <UserManagement />
          </div>
        );
        case "paymentCollection":
  return (
    <div className="content-section">
      <PaymentCollectionDashboard />
    </div>
  );
   case "externalPayment":
  return (
    <div className="content-section">
      <ExternalPayment />
    </div>
  );
      case "analytics":
        return (
          <div className="content-section">
            <Analytics />
          </div>
        );

      case "userInfo":
        return (
          <div className="content-section">
            <h2>User Information</h2>
            {user && (
              <div className="user-details">
                {user.email && (
                  <div className="detail-item">
                    <span className="detail-label">Email:</span>
                    <span className="detail-value">{user.email}</span>
                  </div>
                )}
                {user.name && (
                  <div className="detail-item">
                    <span className="detail-label">Name:</span>
                    <span className="detail-value">{user.name}</span>
                  </div>
                )}
                {user.sub && (
                  <div className="detail-item">
                    <span className="detail-label">User ID:</span>
                    <span className="detail-value">{user.sub}</span>
                  </div>
                )}
                <div className="detail-item">
                  <span className="detail-label">Role:</span>
                  <span
                    className={`detail-value role-${userRole.toLowerCase()}`}
                  >
                    {userRole}
                  </span>
                </div>
                {tokens.idToken && (
                  <div className="detail-item">
                    <span className="detail-label">ID Token (JWT):</span>
                    <div className="token-display">{tokens.idToken}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case "orderManagement":
        return (
          <div className="content-section">
            <h2>Order Management</h2>
            <Orders />
          </div>
        );

      case "inventoryManagement":
        return (
          <div className="content-section">
            <h2>Inventory Management</h2>
            <Inventory />
          </div>
        );

      case "paymentManagement":
        return (
          <div className="content-section">
            <h2>Payment Management</h2>
            <Payments />
          </div>
        );

      case "productCatalog":
        return (
          <div className="content-section">
            <h2>Product Catalog</h2>
            <ProductList />
          </div>
        );

      // Keep existing API testing cases for development
      case "orders":
      case "inventory":
      case "payments":
      case "debugAuth":
        return (
          <div className="content-section">
            <h2>
              {activeSection === "orders" && "Orders API Response"}
              {activeSection === "inventory" && "Inventory API Response"}
              {activeSection === "payments" && "Payments API Response"}
              {activeSection === "debugAuth" && "Debug Auth Response"}
            </h2>
            {apiData && (
              <div className="api-response">
                <pre>{JSON.stringify(apiData, null, 2)}</pre>
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className="content-section">
            Select an option from the sidebar
          </div>
        );
    }
  };

  return (
    <div className="dashboard-layout">
      {/* Header with Logout */}
      <div className="dashboard-header">
        <h1>Cluster Micro Dashboard</h1>
        <div className="header-info">
          <span className={`role-indicator ${isAdmin ? "admin" : "user"}`}>
            {userRole}
          </span>
          <button onClick={logout} className="logout-button">
            Logout
          </button>
        </div>
      </div>

      <div className="dashboard-main">
        {/* Sidebar */}
        <div className="dashboard-sidebar">
          <div className="sidebar-section">
            <h3>MANAGEMENT</h3>
            <button
              className={`sidebar-btn ${
                activeSection === "orderManagement" ? "active" : ""
              }`}
              onClick={() => handleSectionClick("orderManagement")}
              data-section="orderManagement"
            >
              📋 Order Management
            </button>
            <button
              className={`sidebar-btn ${
                activeSection === "inventoryManagement" ? "active" : ""
              }`}
              onClick={() => handleSectionClick("inventoryManagement")}
              data-section="inventoryManagement"
            >
              📦 Inventory Management
            </button>
            <button
              className={`sidebar-btn ${
                activeSection === "paymentManagement" ? "active" : ""
              }`}
              onClick={() => handleSectionClick("paymentManagement")}
              data-section="paymentManagement"
            >
              💳 Payment Management
            </button>
            <button
              className={`sidebar-btn ${
                activeSection === "productCatalog" ? "active" : ""
              }`}
              onClick={() => handleSectionClick("productCatalog")}
              data-section="productCatalog"
            >
              🛍️ Product Catalog
            </button>
            <button
              className={`sidebar-btn ${
                activeSection === "analytics" ? "active" : ""
              }`}
              onClick={() => handleSectionClick("analytics")}
              data-section="analytics"
            >
              📈 Analytics Dashboard
            </button>
          </div>
          <div className="sidebar-section">
            <h3>PAYMENTS</h3>
            <button
              className={`sidebar-btn ${
                activeSection === "makePayment" ? "active" : ""
              }`}
              onClick={() => handleSectionClick("makePayment")}
              data-section="makePayment"
            >
              💳 Make Payment
            </button>
          </div>
<div className="sidebar-section">
  <h3>PAYMENT COLLECTION</h3>
  <button 
    className={`sidebar-btn ${activeSection === 'paymentCollection' ? 'active' : ''}`}
    onClick={() => handleSectionClick('paymentCollection')}
    data-section="paymentCollection"
  >
    💰 Collect Payments
  </button>
   <button
              className={`sidebar-btn ${
                activeSection === "externalPayment" ? "active" : ""
              }`}
              onClick={() => handleSectionClick("externalPayment")}
              data-section="externalPayment"
            >
              🔗 External Payment
            </button>
</div>
          <div className="sidebar-section">
            <h3>USER MANAGEMENT</h3>
            <button
              className={`sidebar-btn ${
                activeSection === "userManagement" ? "active" : ""
              } ${!isAdmin ? "disabled" : ""}`}
              onClick={() => handleSectionClick("userManagement")}
              disabled={!isAdmin}
              data-section="userManagement"
            >
              Manage Users
            </button>
            {!isAdmin && (
              <div className="access-note">Admin access required</div>
            )}
          </div>
        </div>

        {/* Content Area */}
        <div className="dashboard-content">{renderContent()}</div>
      </div>

      {/* Remove the User Creation Modal since it's handled in UserManagement component */}

      <style jsx>{`
        .role-indicator {
          margin-bottom: 1rem;
        }
        .role-badge {
          padding: 0.25rem 0.75rem;
          border-radius: 0.375rem;
          font-size: 0.875rem;
          font-weight: 600;
        }
        .role-badge.admin {
          background-color: #fee2e2;
          color: #dc2626;
        }
        .role-badge.user {
          background-color: #dbeafe;
          color: #2563eb;
        }
        .sidebar-btn.disabled {
          opacity: 0.5;
          cursor: not-allowed;
          background-color: #f3f4f6;
          color: #9ca3af;
        }
        .access-note {
          font-size: 0.75rem;
          color: #6b7280;
          padding: 0.5rem;
          text-align: center;
        }
        .access-denied {
          text-align: center;
          padding: 2rem;
          background-color: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 0.5rem;
        }
        .header-info {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .stat-link.disabled {
          color: #9ca3af;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
